import type { Finding } from '@equiview/protocol';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { CampoAprumo } from '../components/CampoAprumo.js';
import { CampoNumero } from '../components/CampoNumero.js';
import { FotosDaEstacao2 } from '../components/FotosDaEstacao2.js';
import { MarcadoresDaEstacao2 } from '../components/MarcadoresDaEstacao2.js';
import { ManejoCompleto } from '../components/ManejoCompleto.js';
import { NavInterna } from '../components/NavInterna.js';
import { PainelDeEmendas } from '../components/PainelDeEmendas.js';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  Dado,
  Etiqueta,
  EstadoVazio,
  OpcoesRadio,
  Painel,
  Selecao,
  Seletor,
} from '../components/ui/index.js';
import { formatarData, formatarValor, idadeDoAnimal, ROTULO_SEXO, traitPorCodigo } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, FichaDeColeta, FonteDeDados, Manejo, Medida, Pessoa, Usuario } from '../dados/fonte.js';
import { gruposDeColeta, ladosDe, validarFicha } from '../dados/validacao.js';
import { useTecladoAberto } from '../lib/teclado.js';

const INSTRUMENTO: Record<string, string> = {
  hipometro: 'Hipômetro',
  compasso: 'Compasso',
  fita: 'Fita',
  goniometro: 'Goniômetro',
  balanca: 'Balança',
};

const ROTULO_LADO = { E: 'esquerdo', D: 'direito', NA: '' } as const;

const aberta = (a: Pick<Avaliacao, 'status'>) => a.status === 'rascunho' || a.status === 'reaberta';

/** Proxima ficha a coletar na visita, pela ordem do nome. */
export function proximaFicha(
  avaliacoes: readonly Avaliacao[],
  animais: readonly Animal[],
  exceto?: string,
): Avaliacao | undefined {
  const nome = new Map(animais.map((a) => [a.id, a.name]));
  return avaliacoes
    .filter((a) => a.id !== exceto && aberta(a))
    .sort((x, y) => (nome.get(x.animal_id) ?? '').localeCompare(nome.get(y.animal_id) ?? '', 'pt-BR'))[0];
}

/** Minutos entre o inicio e o fim (ou agora) da coleta de uma ficha. */
export function minutosDaFicha(a: Pick<Avaliacao, 'started_at' | 'ended_at'>, agora = Date.now()): number | null {
  if (!a.started_at) return null;
  const fim = a.ended_at ? Date.parse(a.ended_at) : agora;
  return Math.max(0, Math.round((fim - Date.parse(a.started_at)) / 60_000));
}

/** Medidor da ultima ficha desta visita: a dupla costuma ser a mesma o dia todo. */
const chaveMedidor = (visitaId: string) => `equiview:medidor:${visitaId}`;
function lerMedidor(visitaId: string): string {
  try {
    return localStorage.getItem(chaveMedidor(visitaId)) ?? '';
  } catch {
    return '';
  }
}
function lembrarMedidor(visitaId: string, id: string) {
  try {
    localStorage.setItem(chaveMedidor(visitaId), id);
  } catch {
    // Sem armazenamento: so nao lembra.
  }
}

/**
 * Coleta de uma ficha. Nivel 1 (censo): conferencia, peso e ECC e as quatro
 * medidas. Nivel 2 (completa): a Estacao 1 inteira e as 52 medidas da Estacao 3,
 * um instrumento por vez.
 */
export function Coleta() {
  const { id: visitaId = '', fichaId = '' } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(
    async (f) => {
      if (!f.cadastro) return null;
      const [ficha, pessoas, usuario, avaliacoes, animais] = await Promise.all([
        f.cadastro.ficha(fichaId),
        f.cadastro.pessoasDaEquipe(),
        f.usuarioAtual(),
        f.avaliacoes(),
        f.listarAnimais(),
      ]);
      return { ficha, pessoas, usuario, animais, daVisita: avaliacoes.filter((a) => a.visit_id === visitaId) };
    },
    [fichaId, visitaId],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {(d) => {
        if (!d?.ficha || d.ficha.visita.id !== visitaId) {
          return <EstadoVazio titulo="Ficha não encontrada" acao={<BotaoLink to={`/visitas/${visitaId}`}>Voltar à visita</BotaoLink>} />;
        }
        return (
          <FormColeta
            key={fichaId}
            fonte={fonte}
            ficha={d.ficha}
            pessoas={d.pessoas}
            usuario={d.usuario}
            proxima={proximaFicha(d.daVisita, d.animais, fichaId)}
          />
        );
      }}
    </Aguardar>
  );
}

function FormColeta({
  fonte,
  ficha,
  pessoas,
  usuario,
  proxima,
}: {
  fonte: FonteDeDados;
  ficha: FichaDeColeta;
  pessoas: readonly Pessoa[];
  usuario: Usuario | null;
  proxima: Avaliacao | undefined;
}) {
  const cadastro = fonte.cadastro!;
  const navegar = useNavigate();
  const { avaliacao, visita, animal, sessao, manejo } = ficha;
  const nivel = avaliacao.protocol_level;
  const editavel = aberta(avaliacao);
  const medidores = pessoas.filter((p) => p.id !== usuario?.id);
  const grupos = useMemo(() => gruposDeColeta(nivel), [nivel]);

  const [medidor, setMedidor] = useState(() => {
    const lembrado = lerMedidor(visita.id);
    return sessao?.evaluator_id ?? (medidores.some((p) => p.id === lembrado) ? lembrado : '');
  });
  /*
    Dupla medicao: a ficha sorteada ganha uma segunda sessao, de outro avaliador,
    AS CEGAS — no modo 2 a tela so mostra os valores da segunda. A comparacao
    aparece quando a segunda esta completa; antes disso, contaminaria o ICC.
  */
  const sorteada = avaliacao.sampled_for_double_measure;
  const [replica, setReplica] = useState<1 | 2>(1);
  const [medidor2, setMedidor2] = useState(ficha.sessao2?.evaluator_id ?? '');

  /*
    Estudo da Fase 0: cada ficha tem uma sessao por (avaliador, rodada), todas
    cegas. A sessao da vez e escolhida na grade; a tela so mostra os valores dela.
  */
  const estudo = visita.purpose === 'estudo_repetibilidade';
  const [avaliadorEstudo, setAvaliadorEstudo] = useState('');
  const [rodadaEstudo, setRodadaEstudo] = useState<1 | 2>(1);
  const sessaoDoEstudo = ficha.sessoes.find((x) => x.evaluator_id === avaliadorEstudo && x.replicate_no === rodadaEstudo) ?? null;

  const sessaoAtiva = estudo ? sessaoDoEstudo : replica === 1 ? sessao : ficha.sessao2;
  const medidasAtivas = estudo ? (sessaoDoEstudo?.medidas ?? []) : replica === 1 ? ficha.medidas : ficha.medidas2;
  const medidorAtivo = estudo ? avaliadorEstudo : replica === 1 ? medidor : medidor2;
  const replicaAtiva = estudo ? rodadaEstudo : replica;
  const chaveDaSessao = estudo ? `${avaliadorEstudo}-${rodadaEstudo}` : String(replica);
  const nomeDe = (id: string) => pessoas.find((x) => x.id === id)?.nome ?? '—';
  /** Rotulo da sessao de um achado da validacao, pela posicao dela na lista. */
  const rotuloDaSessao = (indice: number | null) => {
    const x = indice == null ? undefined : ficha.sessoes[indice];
    if (!x) return '';
    if (estudo) return ` (${nomeDe(x.evaluator_id)}, rodada ${x.replicate_no})`;
    return x.replicate_no === 2 ? ' (2ª medição)' : '';
  };
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [fechando, setFechando] = useState(false);

  const tentar = async (acao: () => Promise<unknown>) => {
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
      throw e;
    }
  };

  const trocarMedidor = async (id: string) => {
    if (replica === 1) {
      setMedidor(id);
      if (id) lembrarMedidor(visita.id, id);
    } else {
      setMedidor2(id);
    }
    if (!id) return;
    // Com sessao aberta, troca o medidor dela; sem sessao, ela abre na primeira medida.
    if (sessaoAtiva) await tentar(() => cadastro.abrirSessao(avaliacao.id, id, replica)).catch(() => undefined);
  };

  const salvarMedida = (code: string, side: Medida['side'], valor: number | null, naoSeAplica = false) =>
    tentar(async () => {
      const sessaoId = sessaoAtiva?.id ?? (await cadastro.abrirSessao(avaliacao.id, medidorAtivo, replicaAtiva));
      await cadastro.salvarMedida(sessaoId, code, side, valor, naoSeAplica);
    });

  const salvarManejo = (m: Partial<Manejo>) => tentar(() => cadastro.salvarManejo(avaliacao.id, m));

  const medidasDe = (code: string) => medidasAtivas.filter((m) => m.trait_code === code);
  const valorDe = (code: string) => medidasDe(code).find((m) => m.side === 'NA')?.value_num ?? null;
  const preenchida = (code: string, side: Medida['side']) =>
    medidasAtivas.some((m) => m.trait_code === code && m.side === side && (m.value_num != null || m.not_applicable));

  const progresso = grupos.map((g) => {
    const itens = g.traits.flatMap((t) => ladosDe(t).map((side) => preenchida(t.code, side)));
    return { ...g, feitos: itens.filter(Boolean).length, total: itens.length };
  });
  const teclado = useTecladoAberto();
  const feitas = progresso.reduce((n, g) => n + g.feitos, 0);
  const totalDeMedidas = progresso.reduce((n, g) => n + g.total, 0);

  const secoes = useMemo(
    () => [
      { id: 'estacao-0', rotulo: 'Conferência' },
      { id: 'estacao-1', rotulo: 'Estação 1' },
      { id: 'estacao-2', rotulo: 'Fotos' },
      ...grupos.map((g) => ({ id: `grupo-${g.id}`, rotulo: g.rotulo })),
      ...(editavel ? [{ id: 'fechamento', rotulo: 'Fechar' }] : []),
    ],
    [grupos, editavel],
  );

  const resultado = validarFicha(ficha);
  const faltando = resultado.errors.filter((f) => f.ruleId.startsWith('REQ:'));
  const outrosErros = resultado.errors.filter((f) => !f.ruleId.startsWith('REQ:'));
  const avisos = [...new Map(resultado.warnings.map((a) => [a.ruleId, a])).values()];
  const semJustificativa = avisos.filter((a) => (justificativas[a.ruleId] ?? '').trim().length < 10);
  const podeFechar = editavel && !!sessao && resultado.canClose && semJustificativa.length === 0;
  const segundaCompleta =
    !!ficha.sessao2 && !resultado.errors.some((f) => f.ruleId.startsWith('REQ:') && f.sessionIndex === 1);
  const medidoresDaVez = replica === 1 ? medidores : medidores.filter((p) => p.id !== sessao?.evaluator_id);

  const fechar = async () => {
    setFechando(true);
    try {
      await tentar(() =>
        cadastro.fecharFicha(
          avaliacao.id,
          Object.fromEntries(avisos.map((a) => [a.ruleId, justificativas[a.ruleId]!.trim()])),
        ),
      );
    } catch {
      // A mensagem ja esta na tela.
    } finally {
      setFechando(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-4">
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Visitas', para: '/visitas' },
          { rotulo: `Visita nº ${visita.visit_number}`, para: `/visitas/${visita.id}` },
          { rotulo: animal.name },
        ]}
        sobretitulo={nivel === 1 ? 'Censo · Nível 1' : 'Ficha completa · Nível 2'}
        titulo={animal.name}
        etiquetas={
          editavel ? (
            <Etiqueta tom={avaliacao.started_at ? 'aviso' : 'neutro'}>{avaliacao.started_at ? 'Em coleta' : 'A coletar'}</Etiqueta>
          ) : (
            <Etiqueta tom="ok">{avaliacao.status === 'validada' ? 'Ficha validada' : 'Ficha fechada'}</Etiqueta>
          )
        }
        meta={
          <span className="numero">
            {formatarData(visita.visit_date)} · protocolo v1.0
            <Cronometro avaliacao={avaliacao} />
          </span>
        }
        acoes={
          proxima && (
            // No celular ele fica na barra do pe da tela.
            <Botao
              variante={editavel ? 'secundario' : 'principal'}
              className="max-md:hidden"
              onClick={() => navegar(`/visitas/${visita.id}/fichas/${proxima.id}`)}
            >
              Próximo animal
            </Botao>
          )
        }
      />

      {nivel >= 2 && <NavInterna secoes={secoes} />}

      {erro && (
        <p role="alert" className="border-l-2 border-erro bg-erro/8 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      )}

      {replica === 1 && (
        <>
      <Painel id="estacao-0" titulo="Estação 0 · Conferência">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Dado rotulo="Registro">{animal.abccmm_registry ?? '—'}</Dado>
          <Dado rotulo="Sexo">{ROTULO_SEXO[animal.sex]}</Dado>
          <Dado rotulo="Idade na visita">{idadeDoAnimal(animal, visita.visit_date)}</Dado>
          <Dado rotulo="Pelagem">{animal.coat_color ?? '—'}</Dado>
        </dl>
        <p className="mt-3 text-xs text-apagado">
          Confira com o animal na mão. Dado errado?{' '}
          <Link to={`/animais/${animal.id}/editar`} className="text-destaque-texto hover:underline">
            Corrigir o cadastro
          </Link>
          .
        </p>
      </Painel>

      <Painel id="estacao-1" titulo={nivel === 1 ? 'Estação 1 · Peso e condição corporal' : 'Estação 1 · Condição e manejo'}>
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <CampoNumero
                rotulo="Peso"
                valor={manejo.weight_kg}
                casas={0}
                unidade="kg"
                desabilitado={!editavel}
                onSalvar={(v) => salvarManejo({ weight_kg: v })}
              />
              <OpcoesRadio
                rotulo="Método de pesagem"
                valor={manejo.weight_method ?? ''}
                onMudar={(v) => editavel && void salvarManejo({ weight_method: v }).catch(() => undefined)}
                opcoes={[
                  { valor: 'balanca', rotulo: 'Balança' },
                  { valor: 'fita', rotulo: 'Estimativa por fita' },
                ]}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Escore de condição corporal (Henneke)</p>
              <Seletor
                rotulo="Escore de condição corporal"
                valor={manejo.bcs_henneke == null ? null : String(manejo.bcs_henneke)}
                onMudar={(v) => editavel && void salvarManejo({ bcs_henneke: v == null ? null : Number(v) }).catch(() => undefined)}
                opcoes={Array.from({ length: 9 }, (_, i) => ({
                  valor: String(i + 1),
                  conteudo: <span className="numero px-1.5 py-2 text-base">{i + 1}</span>,
                }))}
              />
              <p className="text-xs text-apagado">1 = muito magro · 5 = moderado · 9 = extremamente gordo</p>
            </div>
          </div>
          {nivel >= 2 && <ManejoCompleto manejo={manejo} sexo={animal.sex} desabilitado={!editavel} salvar={salvarManejo} />}
        </div>
      </Painel>

      <Painel
        id="estacao-2"
        titulo="Estação 2 · Fotos"
        acoes={<span className="text-xs text-apagado">Marcadores colados no lado esquerdo</span>}
      >
        <FotosDaEstacao2
          midias={ficha.midias}
          // Foto vale ate depois de a ficha fechar: o servidor aceita midia na ficha fechada.
          editavel
          adicionar={(vista, arquivo) => cadastro.adicionarFoto(avaliacao.id, vista, arquivo)}
          remover={(id) => cadastro.removerMidia(id)}
        />
        {nivel >= 2 && (
          <div className="mt-5 border-t border-borda pt-4">
            <MarcadoresDaEstacao2
              marcadores={ficha.marcadores}
              editavel={editavel}
              marcar={(codigo, colado) => void tentar(() => cadastro.marcarMarcador(avaliacao.id, codigo, colado)).catch(() => undefined)}
            />
          </div>
        )}
      </Painel>
        </>
      )}

      {sorteada && !estudo && (
        <Painel titulo="Dupla medição · ficha sorteada">
          <div className="space-y-4">
            <p className="text-sm text-apagado">
              Esta ficha foi sorteada para a segunda medição: outro avaliador mede de novo a Estação 3, sem ver os valores da
              primeira. É o teste de repetibilidade do método. A ficha só fecha com as duas.
            </p>
            <OpcoesRadio
              rotulo="Medição"
              valor={String(replica) as '1' | '2'}
              onMudar={(v) => setReplica(v === '2' ? 2 : 1)}
              opcoes={[
                { valor: '1', rotulo: 'Primeira medição' },
                { valor: '2', rotulo: 'Segunda medição (cega)' },
              ]}
            />
            {replica === 2 && !sessao && (
              <p className="border-l-2 border-aviso px-3 py-2 text-sm">A segunda medição começa depois da primeira.</p>
            )}
            {segundaCompleta ? (
              <ComparacaoDaDupla ficha={ficha} nivel={nivel} />
            ) : (
              ficha.sessao2 && (
                <p className="text-xs text-apagado">A comparação entre as duas aparece quando a segunda medição estiver completa.</p>
              )
            )}
          </div>
        </Painel>
      )}

      {estudo ? (
        <Painel titulo="Estudo de repetibilidade · Sessões">
          <div className="space-y-4">
            <p className="text-sm text-apagado">
              Cada avaliador mede este animal duas vezes, em rodadas separadas, sem ver os valores das outras sessões. Escolha a
              sessão da vez.
            </p>
            {editavel && (
              <div className="flex flex-wrap items-end gap-4">
                <Selecao rotulo="Avaliador" value={avaliadorEstudo} onChange={(e) => setAvaliadorEstudo(e.target.value)}>
                  <option value="">Escolha o avaliador</option>
                  {medidores.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.nome}
                    </option>
                  ))}
                </Selecao>
                <OpcoesRadio
                  rotulo="Rodada"
                  valor={String(rodadaEstudo) as '1' | '2'}
                  onMudar={(v) => setRodadaEstudo(v === '2' ? 2 : 1)}
                  opcoes={[
                    { valor: '1', rotulo: '1ª rodada' },
                    { valor: '2', rotulo: '2ª rodada' },
                  ]}
                />
              </div>
            )}
            {ficha.sessoes.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {ficha.sessoes.map((x) => {
                  const esperados = gruposDeColeta(nivel).flatMap((g) => g.traits.flatMap((t) => ladosDe(t))).length;
                  const feitos = x.medidas.filter((m) => m.value_num != null || m.not_applicable).length;
                  const ativa = x.evaluator_id === avaliadorEstudo && x.replicate_no === rodadaEstudo;
                  return (
                    <li key={x.id}>
                      <button
                        type="button"
                        aria-pressed={ativa}
                        onClick={() => {
                          setAvaliadorEstudo(x.evaluator_id);
                          setRodadaEstudo(x.replicate_no === 2 ? 2 : 1);
                        }}
                        className={`min-h-11 rounded-(--radius-controle) border px-3 text-sm ${
                          ativa ? 'border-marca bg-marca text-sobre-marca' : 'border-borda-forte hover:border-destaque'
                        }`}
                      >
                        {nomeDe(x.evaluator_id)} · rodada {x.replicate_no}{' '}
                        <span className={`numero text-xs ${feitos >= esperados ? '' : 'opacity-70'}`}>
                          {feitos}/{esperados}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Painel>
      ) : (
      <Painel titulo={replica === 1 ? 'Estação 3 · Quem mede' : 'Segunda medição · Quem mede'}>
        {editavel ? (
          <div className="space-y-3">
            <Selecao
              rotulo={replica === 1 ? 'Quem está medindo' : 'Segundo avaliador'}
              ajuda={
                replica === 1
                  ? 'Quem registra é você. Quem mede nunca é quem registra.'
                  : 'Outro avaliador, diferente do da primeira medição e de quem registra.'
              }
              value={medidorAtivo}
              onChange={(e) => void trocarMedidor(e.target.value)}
            >
              <option value="">Escolha o medidor</option>
              {medidoresDaVez.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </Selecao>
            {medidoresDaVez.length === 0 && (
              <p className="border-l-2 border-aviso px-3 py-2 text-sm">
                Nenhuma outra pessoa da equipe neste aparelho. O medidor precisa estar cadastrado pelo admin e sincronizado.
              </p>
            )}
            {nivel >= 2 && (
              <p className="text-xs text-apagado">Enter leva ao próximo campo. As medidas estão na ordem dos instrumentos.</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-apagado">
            Medido por {pessoas.find((p) => p.id === sessao?.evaluator_id)?.nome ?? '—'}.
          </p>
        )}
      </Painel>
      )}

      {progresso.map((g) => (
        <Painel
          key={`${chaveDaSessao}-${g.id}`}
          id={`grupo-${g.id}`}
          titulo={`${
            estudo ? (avaliadorEstudo ? `${nomeDe(avaliadorEstudo)} · rodada ${rodadaEstudo} · ` : '') : replica === 2 ? '2ª medição · ' : ''
          }${nivel === 1 ? 'Estação 3 · Medidas' : g.rotulo}`}
          acoes={
            <span className={`numero text-xs ${g.feitos === g.total ? 'text-ok' : 'text-apagado'}`}>
              {g.feitos}/{g.total}
            </span>
          }
        >
          {g.id === 'aprumos' ? (
            <div className="space-y-4">
              {g.traits.map((t) => (
                <CampoAprumo
                  key={t.code}
                  trait={t}
                  valores={medidasDe(t.code)}
                  desabilitado={!editavel || !medidorAtivo || (!estudo && replica === 2 && !sessao)}
                  onSalvar={(side, valor, na) => void salvarMedida(t.code, side, valor, na).catch(() => undefined)}
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2">
              {g.traits.map((t) => (
                <CampoNumero
                  key={t.code}
                  rotulo={nivel === 1 ? t.label : `${t.label} · ${t.code}`}
                  valor={valorDe(t.code)}
                  casas={t.decimals}
                  unidade={t.unit === 'graus' ? '°' : t.unit}
                  faixa={{ min: t.minPlausible, max: t.maxPlausible }}
                  ajuda={nivel === 1 && t.instrument ? INSTRUMENTO[t.instrument] : undefined}
                  desabilitado={!editavel || !medidorAtivo || (!estudo && replica === 2 && !sessao)}
                  onSalvar={(v) => salvarMedida(t.code, 'NA', v)}
                />
              ))}
            </div>
          )}
        </Painel>
      ))}

      {editavel && (
        <Painel id="fechamento" titulo="Conferência antes de fechar">
          <div className="space-y-4">
            {faltando.length > 0 && (
              <div>
                <p className="text-sm font-medium">
                  {faltando.length === 1 ? 'Falta 1 item:' : `Faltam ${faltando.length} itens:`}
                </p>
                <p className="mt-1 text-sm text-apagado">{faltando.map((f) => itemFaltando(f) + rotuloDaSessao(f.sessionIndex)).join(' · ')}</p>
              </div>
            )}
            {outrosErros.length > 0 && (
              <div>
                <p className="text-sm font-medium">Precisa resolver:</p>
                <ul className="mt-1 list-inside list-disc text-sm text-apagado">
                  {outrosErros.map((f) => (
                    <li key={`${f.ruleId}-${f.sessionIndex}`}>{textoDoAchado(f)}</li>
                  ))}
                </ul>
                {outrosErros.some((f) => f.ruleId === 'PRD-003') && (
                  <Botao
                    tamanho="sm"
                    className="mt-2"
                    onClick={() => void tentar(() => cadastro.mudarNivel(avaliacao.id, 1)).catch(() => undefined)}
                  >
                    Passar para o censo (Nível 1)
                  </Botao>
                )}
              </div>
            )}
            {resultado.errors.length === 0 && <p className="text-sm text-ok">Nenhum erro: o protocolo está completo.</p>}
            {avisos.map((a) => (
              <div key={a.ruleId} className="space-y-1.5 border-l-2 border-aviso pl-3">
                <p className="text-sm">
                  <span className="font-medium text-aviso">Aviso:</span> {textoDoAchado(a)}
                </p>
                <textarea
                  aria-label={`Justificativa para ${a.ruleId}`}
                  placeholder="Por que o valor está certo? (mínimo 10 caracteres)"
                  rows={2}
                  value={justificativas[a.ruleId] ?? ''}
                  onChange={(e) => setJustificativas((j) => ({ ...j, [a.ruleId]: e.target.value }))}
                  className="w-full rounded-(--radius-controle) border border-borda-forte bg-superficie px-3 py-2 text-sm outline-none focus:border-destaque"
                />
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3">
              <Botao variante="principal" disabled={!podeFechar} carregando={fechando} onClick={() => void fechar()}>
                Fechar ficha
              </Botao>
              <span className="text-xs text-apagado">Depois de fechada, correção só por emenda.</span>
            </div>
          </div>
        </Painel>
      )}

      {!editavel && <PainelDeEmendas ficha={ficha} usuario={usuario} />}

      {!editavel && !proxima && (
        <EstadoVazio titulo="Coleta desta visita concluída" acao={<BotaoLink to={`/visitas/${visita.id}`}>Voltar à visita</BotaoLink>} />
      )}
      {/*
        Celular: a coleta se faz com o tablet ou o celular numa mao so. O pe da
        tela mostra de quem e a ficha, quanto falta e o proximo animal, sem
        precisar voltar ao topo. (A navegacao de baixo sai: e tela de tarefa.)
      */}
      <div
        className={`fixed inset-x-0 bottom-0 z-20 border-t border-borda bg-superficie/95 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur md:hidden ${
          teclado ? 'hidden' : ''
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-[15px] leading-tight font-semibold text-marca">{animal.name}</p>
            <p className="numero text-xs text-apagado">
              {editavel ? `${feitas}/${totalDeMedidas} medidas` : avaliacao.status === 'validada' ? 'Ficha validada' : 'Ficha fechada'}
              {editavel && faltando.length > 0 && ` · faltam ${faltando.length}`}
            </p>
          </div>
          {proxima ? (
            <Botao variante="principal" onClick={() => navegar(`/visitas/${visita.id}/fichas/${proxima.id}`)}>
              Próximo animal
            </Botao>
          ) : (
            editavel && (
              <Botao onClick={() => document.getElementById('fechamento')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                Ir ao fechamento
              </Botao>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/** Tempo da ficha no cabecalho: correndo durante a coleta, fixo depois de fechada. */
function Cronometro({ avaliacao }: { avaliacao: Avaliacao }) {
  const [agora, setAgora] = useState(Date.now());
  useEffect(() => {
    if (!avaliacao.started_at || avaliacao.ended_at) return;
    const t = window.setInterval(() => setAgora(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [avaliacao.started_at, avaliacao.ended_at]);
  const min = minutosDaFicha(avaliacao, agora);
  if (min == null) return null;
  return <> · {avaliacao.ended_at ? `coletada em ${min} min` : `em coleta há ${min} min`}</>;
}

/** "Altura na base do pescoco", "Carpo (frente), esquerdo". A sessao vem de quem chama. */
function itemFaltando(f: Finding): string {
  const t = traitPorCodigo.get(f.traits[0] ?? '');
  const lado = f.side && f.side !== 'NA' ? `, ${ROTULO_LADO[f.side]}` : '';
  return `${t?.label ?? f.traits[0]}${lado}`;
}

/**
 * Primeira x segunda medicao, lado a lado. So aparece com a segunda completa:
 * e o retrato da repetibilidade daquele animal. Diferenca grande nao bloqueia
 * nada — e dado para o ICC, nao erro de digitacao —, so chama a atencao.
 */
function ComparacaoDaDupla({ ficha, nivel }: { ficha: FichaDeColeta; nivel: number }) {
  const linhas = gruposDeColeta(nivel).flatMap((g) =>
    g.traits.flatMap((t) =>
      ladosDe(t).map((side) => {
        const achar = (ms: readonly Medida[]) => ms.find((m) => m.trait_code === t.code && m.side === side);
        return { t, side, a: achar(ficha.medidas), b: achar(ficha.medidas2) };
      }),
    ),
  );
  const tolerancia = (unit: string) => (unit === 'graus' ? 2 : unit === 'escore' ? 0 : 1);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-borda-forte text-left text-xs text-apagado">
            <th className="py-1 font-medium">Medida</th>
            <th className="py-1 text-right font-medium">1ª</th>
            <th className="py-1 text-right font-medium">2ª</th>
            <th className="py-1 text-right font-medium">Diferença</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ t, side, a, b }) => {
            const va = a?.not_applicable ? null : (a?.value_num ?? null);
            const vb = b?.not_applicable ? null : (b?.value_num ?? null);
            const dif = va != null && vb != null ? vb - va : null;
            const longe = dif != null && Math.abs(dif) > tolerancia(t.unit);
            return (
              <tr key={`${t.code}-${side}`} className="border-b border-borda">
                <td className="py-1">
                  {t.label}
                  {side !== 'NA' && <span className="text-apagado"> · {ROTULO_LADO[side]}</span>}
                </td>
                <td className="numero py-1 text-right">{va == null ? 'N/A' : formatarValor(va, t)}</td>
                <td className="numero py-1 text-right">{vb == null ? 'N/A' : formatarValor(vb, t)}</td>
                <td className={`numero py-1 text-right ${longe ? 'font-semibold text-aviso' : 'text-apagado'}`}>
                  {dif == null ? '—' : `${dif > 0 ? '+' : dif < 0 ? '−' : ''}${formatarValor(Math.abs(dif), t)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Mensagem do protocolo, com o valor observado quando houver. */
function textoDoAchado(f: Finding): string {
  return f.observed ? `${f.message} (${f.observed})` : f.message;
}
